import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import axios from "axios";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  InputAdornment,
  Select,
  Stack,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  BadgeOutlined as BadgeOutlinedIcon,
  BusinessOutlined as BusinessOutlinedIcon,
  CalendarTodayOutlined as CalendarIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  DragIndicator as DragIndicatorIcon,
  DrawOutlined as DrawOutlinedIcon,
  EmailOutlined as EmailOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  HomeOutlined as HomeOutlinedIcon,
  KeyOutlined as KeyOutlinedIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  LocalOfferOutlined as LocalOfferOutlinedIcon,
  MonetizationOnOutlined as MonetizationOnOutlinedIcon,
  NumbersOutlined as NumbersOutlinedIcon,
  PersonOutline as PersonOutlineIcon,
  PhoneOutlined as PhoneOutlinedIcon,
  StarOutline as StarOutlineIcon,
  Tune as TuneIcon,
  Search as SearchIcon,
  VerifiedOutlined as VerifiedOutlinedIcon,
} from "@mui/icons-material";
import { FavouriteDragItem } from "./FavouriteDragAndDrop";

const FAVOURITES_STORAGE_KEY = "pdf-editor-field-favourites-v1";
const FAVOURITE_CATEGORIES_STORAGE_KEY = "pdf-editor-favourite-categories-v1";

const FAVOURITE_CATEGORIES = [
  { key: "project", label: "Project", color: "#64748b" },
  { key: "owner", label: "Owner", color: "#2563eb" },
  { key: "contractor", label: "Contractor", color: "#d97706" },
  { key: "ownerSignature", label: "Owner signature", color: "#2563eb" },
  {
    key: "contractorSignature",
    label: "Contractor signature",
    color: "#d97706",
  },
  {
    key: "ownerNotarization",
    label: "Owner notarization",
    color: "#2563eb",
  },
  {
    key: "contractorNotarization",
    label: "Contractor notarization",
    color: "#d97706",
  },
  { key: "other", label: "Other", color: "#64748b" },
];

const CATEGORY_COLOR_OPTIONS = [
  { label: "Slate", value: "#64748b" },
  { label: "Blue", value: "#2563eb" },
  { label: "Orange", value: "#d97706" },
];

const FAVOURITE_ICON_OPTIONS = {
  description: {
    label: "Document",
    icon: <DescriptionOutlinedIcon fontSize="small" />,
  },
  number: { label: "Number", icon: <NumbersOutlinedIcon fontSize="small" /> },
  location: {
    label: "Location",
    icon: <LocationOnOutlinedIcon fontSize="small" />,
  },
  person: { label: "Person", icon: <PersonOutlineIcon fontSize="small" /> },
  business: {
    label: "Business",
    icon: <BusinessOutlinedIcon fontSize="small" />,
  },
  home: { label: "Property", icon: <HomeOutlinedIcon fontSize="small" /> },
  calendar: { label: "Date", icon: <CalendarIcon fontSize="small" /> },
  tag: { label: "Tag", icon: <LocalOfferOutlinedIcon fontSize="small" /> },
  money: {
    label: "Money",
    icon: <MonetizationOnOutlinedIcon fontSize="small" />,
  },
  phone: { label: "Phone", icon: <PhoneOutlinedIcon fontSize="small" /> },
  email: { label: "Email", icon: <EmailOutlinedIcon fontSize="small" /> },
  badge: {
    label: "ID / License",
    icon: <BadgeOutlinedIcon fontSize="small" />,
  },
  key: { label: "Key", icon: <KeyOutlinedIcon fontSize="small" /> },
  signature: {
    label: "Signature",
    icon: <DrawOutlinedIcon fontSize="small" />,
  },
  seal: {
    label: "Notary seal",
    icon: <VerifiedOutlinedIcon fontSize="small" />,
  },
};

const DEFAULT_FAVOURITE_TEMPLATES = [
  [
    "0750477e-f7f9-42e7-a2c8-a3a39a416cd1",
    "project",
    "Permit Number",
    "number",
  ],
  [
    "5a86bb6d-ba11-40c6-a033-d31ff4c47cd8",
    "project",
    "Tax Folio/Parcel ID",
    "tag",
  ],
  [
    "c8f429d2-5fdd-4ca1-9b21-514633cc148b",
    "project",
    "Property/Project Full Address",
    "location",
  ],
  ["55186a2a-bd41-47d8-b2ff-e154840e05cb", "owner", "Owner Name", "person"],
  ["7905afa1-3413-4400-850e-a326dcc7e565", "owner", "Owner Phone", "phone"],
  ["a4c20b30-99a6-4d24-9657-bf3582e52897", "owner", "Owner Fax", "phone"],
  ["0d27a461-8fca-420e-a91e-26d72cf0cd2e", "owner", "Owner Email", "email"],
  [
    "6a9e9640-986c-468d-8cdb-d845278f092a",
    "owner",
    "Owner Full Address",
    "home",
  ],
  [
    "39586a3c-58a6-4d16-9c18-f38d0e9cf86a",
    "contractor",
    "Contractor License Number",
    "badge",
  ],
  [
    "bc0dacf3-03ca-4dde-a33a-2a63d86a1c76",
    "contractor",
    "Contractor Company Name",
    "business",
  ],
  [
    "fe58f065-4277-4848-8820-dd296d4fccaa",
    "contractor",
    "Contractor Personal Name",
    "person",
  ],
  [
    "88cd5fb0-81ea-47e9-baea-27e3dc543944",
    "contractor",
    "Contractor Initial",
    "person",
  ],
  [
    "b454bb8c-be64-4948-ba59-842451c52196",
    "contractor",
    "Contractor Phone",
    "phone",
  ],
  [
    "5f28058f-5a61-4e2b-96a6-c9a2ccae67eb",
    "contractor",
    "Contractor Fax",
    "phone",
  ],
  [
    "46b64e25-a7ca-493b-bcf2-420b874e2800",
    "contractor",
    "Contractor Email",
    "email",
  ],
  [
    "44c70798-bbf0-4675-a4fe-d2ceb6630a58",
    "contractor",
    "Contractor Full Address",
    "home",
  ],
  [
    "d4bf10f9-f81c-44d5-8fcf-822ad4aa16dc",
    "ownerNotarization",
    "Owner's Identification Type",
    "badge",
  ],
  [
    "08b987a9-7b94-4092-bf72-00f7ae935deb",
    "ownerSignature",
    "Owner Signature",
    "signature",
  ],
  [
    "3077ea62-4fc0-4144-a56d-971903d5e941",
    "ownerSignature",
    "Owner Signature Print Name",
    "person",
  ],
  [
    "cb6c9096-aad8-4371-bc9d-be8101c9b076",
    "ownerSignature",
    "Owner's Role Title",
    "tag",
  ],
  [
    "ba304e15-d71e-4d90-81f9-6d186c9b9993",
    "ownerSignature",
    "Owner Signature Full Address",
    "home",
  ],
  [
    "e5526a35-3789-481f-b22c-f3df6da969bb",
    "contractorNotarization",
    "Contractor's Identification Type",
    "badge",
  ],
  [
    "875f921a-49d5-4bef-87c4-ea96bf640cdd",
    "contractorSignature",
    "Contractor Signature",
    "signature",
  ],
  [
    "9b1ff0a0-d440-4b2f-a0a9-0a13a839e522",
    "ownerNotarization",
    "Owner's Notary Seal",
    "seal",
  ],
  [
    "b1b7f13b-3b21-4c41-be93-0755e60c9225",
    "ownerNotarization",
    "Owner's Notary Signature",
    "signature",
  ],
  [
    "117e598f-4e1f-4cba-a421-71085cdca095",
    "ownerNotarization",
    "Owner's Notary Signature Print Name",
    "person",
  ],
  [
    "5811a7ab-4069-4df3-8952-248e96fc93ca",
    "contractorNotarization",
    "Contractor's Notary Seal",
    "seal",
  ],
  [
    "843f5f8c-118b-4d7d-92c1-66fdea2310a3",
    "contractorNotarization",
    "Contractor's Notary Signature",
    "signature",
  ],
  [
    "73994072-5ffe-4bf2-9182-e6ddc5f52b2c",
    "contractor",
    "Contractor Street",
    "home",
  ],
  [
    "77fc2668-bf27-4aca-b9ed-dfacacb2e48a",
    "contractor",
    "Contractor City",
    "home",
  ],
  [
    "a23469a2-a6f8-4644-bc7e-cf66d4c56593",
    "contractor",
    "Contractor State",
    "home",
  ],
  [
    "1c6d214d-bc2d-476a-a595-df6e2f63cbd5",
    "contractor",
    "Contractor Zip Code",
    "home",
  ],
  [
    "4df36570-6553-4b81-8949-e1ea49d2b8c4",
    "project",
    "Property/Project Street",
    "home",
  ],
  [
    "0f57b951-2459-4e2b-855a-0969f3195646",
    "project",
    "Property/Project City",
    "home",
  ],
  [
    "cca8174b-4d3c-4a2b-9ea1-4b368c43e2e8",
    "project",
    "Property/Project State",
    "home",
  ],
  [
    "42d579cd-fd1d-448c-8283-b8eb3ddd0116",
    "project",
    "Property/Project Zip",
    "home",
  ],
  [
    "8a17e71f-13e6-46e0-9fa4-2e46273d765c",
    "ownerNotarization",
    "Owner's Notarization Day",
    "calendar",
  ],
  [
    "87a8545d-ba77-43fa-adb6-bde3169f04f7",
    "ownerNotarization",
    "Owner's Notarization Month",
    "calendar",
  ],
  [
    "be28843b-800a-4700-97e9-78e8f82bc8a2",
    "ownerNotarization",
    "Owner's Notarization Year (YY)",
    "calendar",
  ],
  [
    "7ccf9ff3-7fec-44ec-88bf-1a1232a071d7",
    "ownerNotarization",
    "Owner's Notarization Year (YYYY)",
    "calendar",
  ],
  [
    "8bb335fc-3a0a-415d-9e21-d64fdbc650b7",
    "ownerNotarization",
    "Physical Presence",
    "seal",
  ],
  [
    "643c70d3-fc5c-44b9-8ef7-03132ce8c108",
    "ownerNotarization",
    "Online Notarization",
    "seal",
  ],
  [
    "25ad91f1-9dd0-416d-afd6-9c0363679765",
    "contractorNotarization",
    "Contractor's Notary Signature Print Name",
    "person",
  ],
  [
    "ebdc1286-ba2c-4412-8652-222e42d597d5",
    "contractorNotarization",
    "Contractor's Notarization Day",
    "calendar",
  ],
  [
    "a7236d1f-0369-4ba0-9ae1-9ee37fc80f1f",
    "contractorNotarization",
    "Contractor's Notarization Month",
    "calendar",
  ],
  [
    "e9c4461d-bb5a-40e7-81fb-cc377ef92a18",
    "contractorNotarization",
    "Contractor's Notarization Year (YY)",
    "calendar",
  ],
  [
    "1388c90f-c981-4b5f-9bce-7240f4f3b251",
    "contractorNotarization",
    "Contractor's Notarization Year (YYYY)",
    "calendar",
  ],
  [
    "dd6fd0a1-51ad-4c9e-8f0d-b2bdf49b14a5",
    "contractorNotarization",
    "Physical Presence",
    "seal",
  ],
  [
    "42c67a5e-e1dd-4397-828b-ab4a39045372",
    "contractorNotarization",
    "Online Notarization",
    "seal",
  ],
  [
    "2cf0fe18-fd8b-4fec-a62b-8e26f992b3d5",
    "contractorSignature",
    "Contractor Signature Print Name",
    "person",
  ],
  [
    "d3d0a81f-daed-4abc-8e1d-39d44668a05c",
    "contractorSignature",
    "Contractor Signature Full Address",
    "home",
  ],
  [
    "e39e294c-d879-42b5-8a93-1314028b24a5",
    "project",
    "Legal Description",
    "key",
  ],
  [
    "e0347ec6-6141-4d4b-a048-94f881c717a9",
    "project",
    "General Description of Improvement - NOC",
    "description",
  ],
  ["3e9d0b68-6531-4eaf-b48e-ef6a699b1cfc", "project", "Project Name", "tag"],
  [
    "65330fbd-2bf5-421b-9ff7-dc786cc8a700",
    "ownerNotarization",
    "Owner's Notary Commission Expiration Date",
    "calendar",
  ],
  [
    "33e8bb8b-fd55-4d28-b528-865d5364e571",
    "ownerNotarization",
    "Personally Known",
    "tag",
  ],
  [
    "03adb45e-fc56-41ea-be40-174a2eb408c7",
    "ownerNotarization",
    "Produced Identification",
    "tag",
  ],
  [
    "955a1a65-4b7e-4864-879d-db7da0e31bc9",
    "project",
    "Description of Work",
    "description",
  ],
  ["2d182519-63b5-4836-bf6b-799ba13109d9", "project", "Job Value", "money"],
  [
    "1f9d0f41-26cb-4f47-b060-a44cffb3300f",
    "ownerSignature",
    "Owner Signature County",
    "home",
  ],
  [
    "c3955f34-9b4c-4f60-b3d2-7b03d65bb9de",
    "contractorSignature",
    "Contractor Signature State",
    "home",
  ],
  [
    "120944e8-e0b0-4171-b56b-4fdacfc2002a",
    "contractorSignature",
    "Contractor Signature County",
    "home",
  ],
  [
    "cc3600b9-453c-4e03-ad30-6f1f683211a0",
    "contractorNotarization",
    "Personally Known",
    "tag",
  ],
  [
    "b661eecc-8890-48e3-8b85-010b2a6fa6ad",
    "contractorNotarization",
    "Produced Identification",
    "tag",
  ],
  [
    "29d425b8-dc9e-4acb-9f00-a17ec0c69a43",
    "contractorSignature",
    "Contractor Signature Date",
    "calendar",
  ],
  [
    "baedd242-1e3c-4955-8e9c-8166e213b924",
    "ownerSignature",
    "Owner Signature Date",
    "calendar",
  ],
  ["2c8f5458-f44c-4715-ad33-c9a718a5009c", "owner", "Owner Street", "home"],
  ["67c021c8-2e7f-45ca-abc7-59be37fd3105", "owner", "Owner City", "home"],
  ["99ca85b3-ba1d-4ce7-9e69-23f6ce0f9314", "owner", "Owner State", "home"],
  ["c64a67bd-5a23-4d5e-986a-80e1b008e994", "owner", "Owner Zip Code", "home"],
  [
    "3891bc47-7071-4796-a830-9eb148b3defc",
    "ownerNotarization",
    "Owner's Notary County",
    "home",
  ],
  [
    "a783b836-0d9e-4d73-8f4d-ba22f69b9c55",
    "contractorNotarization",
    "Contractor's Notary County",
    "home",
  ],
].map(([fieldId, category, label, icon]) => ({
  fieldId,
  category,
  label,
  icon,
}));

const normalizeText = (value) =>
  String(value ?? "")
    .replace(/[’']/g, "'")
    .replace(/'s\b/gi, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const getFieldId = (field) =>
  field?.field_id ?? field?.id ?? field?.field_name ?? field?.name;

const getFieldLabel = (field) =>
  field?.field_label ??
  field?.label ??
  field?.field_name ??
  field?.name ??
  "Field";

const FavouriteSearchField = React.memo(({ onSearch }) => {
  const [value, setValue] = useState("");
  const timeoutRef = useRef(null);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleChange = (event) => {
    const next = event.target.value;
    setValue(next);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => onSearch(next), 200);
  };

  const handleClear = () => {
    clearTimeout(timeoutRef.current);
    setValue("");
    onSearch("");
  };

  return (
    <TextField
      size="small"
      fullWidth
      label="Search favorites"
      value={value}
      onChange={handleChange}
      sx={{ mb: 1.5 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={handleClear}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
});

export const flattenFavouriteFields = (fields = []) => {
  const fieldsById = new Map();
  const parentById = new Map();

  const visit = (items, parent = null, isOption = false) => {
    if (!Array.isArray(items)) return;

    items.forEach((item) => {
      if (!item || typeof item !== "object") return;

      const fieldId = isOption
        ? (item.field_id ?? item.value ?? item.id)
        : getFieldId(item);

      const parentId =
        item.parent_field_id ?? (parent ? getFieldId(parent) : null);

      const normalizedField = {
        ...item,
        field_id: fieldId,
        field_label:
          item.field_label || item.label || item.field_name || item.name || "",
        location: item.location ?? parent?.location ?? "create",
      };

      if (fieldId !== undefined && fieldId !== null && fieldId !== "") {
        const key = String(fieldId);

        if (!fieldsById.has(key)) {
          fieldsById.set(key, normalizedField);
        }

        // Capture parent information even for a duplicate option.
        if (
          parentId !== undefined &&
          parentId !== null &&
          parentId !== "" &&
          String(parentId) !== key
        ) {
          parentById.set(key, String(parentId));
        }
      }

      visit(item.children, normalizedField);
      visit(item.options, normalizedField, true);
    });
  };

  visit(fields);

  // Resolve parents after collecting the complete field list.
  return [...fieldsById.entries()].map(([key, field]) => {
    const parentId = parentById.get(key);
    const parent = parentId ? fieldsById.get(parentId) : null;

    return {
      ...field,
      ...(parentId ? { parent_field_id: parentId } : {}),
      favourite_parent_label: parent ? getFieldLabel(parent) : "",
    };
  });
};

const createDefaultFavourites = (fields) => {
  const availableFieldIds = new Set(
    fields.map((field) => String(getFieldId(field))).filter(Boolean),
  );

  return DEFAULT_FAVOURITE_TEMPLATES.reduce((favourites, template, index) => {
    if (!availableFieldIds.has(template.fieldId)) return favourites;

    favourites.push({
      id: `default-${template.category}-${template.fieldId}-${index}`,
      fieldId: template.fieldId,
      category: template.category,
      label: template.label,
      icon: template.icon,
    });
    return favourites;
  }, []);
};

const getAutomaticCategories = (signatures = {}) => {
  const categories = [];

  ["owner", "contractor"].forEach((person) => {
    const selection = signatures?.[person];

    if (selection?.sign === true) {
      categories.push(`${person}Signature`);
    }

    if (selection?.notary === true) {
      categories.push(`${person}Notarization`);
    }
  });

  return categories;
};

const readStoredFavourites = () => {
  try {
    if (typeof window === "undefined") return null;

    const raw = window.localStorage.getItem(FAVOURITES_STORAGE_KEY);
    if (raw === null) return null;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;

    const seenIds = new Set();

    return parsed.filter((item) => {
      if (
        !item ||
        typeof item.id !== "string" ||
        !["string", "number"].includes(typeof item.fieldId) ||
        typeof item.label !== "string" ||
        typeof item.category !== "string" ||
        !item.category.trim() ||
        seenIds.has(item.id)
      ) {
        return false;
      }

      seenIds.add(item.id);
      return true;
    });
  } catch {
    return null;
  }
};

const readStoredFavouriteCategories = () => {
  try {
    const raw = window.localStorage.getItem(FAVOURITE_CATEGORIES_STORAGE_KEY);

    if (!raw) return FAVOURITE_CATEGORIES;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return FAVOURITE_CATEGORIES;

    const seen = new Set();

    const validCategories = parsed.filter((category) => {
      if (
        !category ||
        typeof category.key !== "string" ||
        !category.key.trim() ||
        typeof category.label !== "string" ||
        !category.label.trim() ||
        seen.has(category.key)
      ) {
        return false;
      }

      seen.add(category.key);
      return true;
    });

    // Keep Other available as the destination for removed categories.
    if (!seen.has("other")) {
      validCategories.push(
        FAVOURITE_CATEGORIES.find((category) => category.key === "other"),
      );
    }

    return validCategories;
  } catch {
    return FAVOURITE_CATEGORIES;
  }
};

const getFavouriteOptionLabel = (field) => {
  const label = getFieldLabel(field);

  return field?.favourite_parent_label
    ? `${label} (${field.favourite_parent_label})`
    : label;
};

const FavouriteDraftTextField = React.memo(
  React.forwardRef(function FavouriteDraftTextField(
    { onChange, ...props },
    ref,
  ) {
    const [value, setValue] = useState("");
    const valueRef = useRef("");

    React.useImperativeHandle(
      ref,
      () => ({
        getValue: () => valueRef.current,
        setValue: (nextValue) => {
          valueRef.current = nextValue;
          setValue(nextValue);
        },
      }),
      [],
    );

    return (
      <TextField
        {...props}
        value={value}
        onChange={(event) => {
          valueRef.current = event.target.value;
          setValue(event.target.value);
          onChange?.(event);
        }}
      />
    );
  }),
);

const FavouritesBar = ({
  fields = [],
  onFavouriteSelect,
  signerContext,
  loading = false,
  currentSession,
  onNotify,
}) => {
  const [categories, setCategories] = useState(FAVOURITE_CATEGORIES);

  const newCategoryInputRef = useRef(null);
  const tooltipLabelInputRef = useRef(null);
  const [categoryError, setCategoryError] = useState("");
  const [newCategoryColor, setNewCategoryColor] = useState("#64748b");

  const availableFields = useMemo(
    () => flattenFavouriteFields(fields),
    [fields],
  );
  const [expanded, setExpanded] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [draggedFavouriteId, setDraggedFavouriteId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(
    () => new Set(["project", "owner", "contractor", "other"]),
  );
  const [draftFavourite, setDraftFavourite] = useState({
    fieldId: "",
    category: "project",
    label: "",
    icon: "description",
  });
  const [favourites, setFavourites] = useState([]);
  const [preferencesHydrated, setPreferencesHydrated] = useState(false);
  const initializedRef = useRef(false);
  const serverPreferencesExistRef = useRef(false);
  const favouritesRef = useRef([]);
  const categoriesRef = useRef(FAVOURITE_CATEGORIES);
  const saveQueueRef = useRef(Promise.resolve());
  const onNotifyRef = useRef(onNotify);

  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const autocompleteInputRef = useRef(null);

  const automaticCategories = useMemo(
    () =>
      getAutomaticCategories(signerContext).filter((key) =>
        categories.some((category) => category.key === key),
      ),
    [signerContext, categories],
  );

  const automaticCategoryKey = automaticCategories.join("|");

  const previousAutomaticCategoriesRef = useRef(new Set());

  useEffect(() => {
    favouritesRef.current = favourites;
  }, [favourites]);

  useEffect(() => {
    categoriesRef.current = categories;
  }, [categories]);

  useEffect(() => {
    onNotifyRef.current = onNotify;
  }, [onNotify]);

  const persistPreferences = useCallback(
    (nextFavourites, nextCategories, onPersisted) => {
      if (!currentSession) return Promise.resolve();

      saveQueueRef.current = saveQueueRef.current
        .catch(() => undefined)
        .then(() =>
          axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/form-field-favourites/save`,
            {
              currentSession,
              favourites: nextFavourites,
              categories: nextCategories,
            },
            { withCredentials: true },
          ),
        )
        .then((response) => {
          onPersisted?.(response);
          return response;
        })
        .catch(() => {
          onNotifyRef.current?.(
            "error",
            "Your favorites could not be saved. Please try again.",
          );
        });

      return saveQueueRef.current;
    },
    [currentSession],
  );

  const persistFavouriteDeletion = useCallback(
    (favouriteId) => {
      if (!currentSession) return Promise.resolve();

      saveQueueRef.current = saveQueueRef.current
        .catch(() => undefined)
        .then(() =>
          axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/form-field-favourites/delete`,
            { currentSession, favouriteId },
            { withCredentials: true },
          ),
        )
        .catch(() => {
          onNotifyRef.current?.(
            "error",
            "Your favorite could not be deleted. Please try again.",
          );
        });

      return saveQueueRef.current;
    },
    [currentSession],
  );

  const commitPreferences = useCallback(
    (nextFavourites, nextCategories) => {
      favouritesRef.current = nextFavourites;
      categoriesRef.current = nextCategories;
      setFavourites(nextFavourites);
      setCategories(nextCategories);
      persistPreferences(nextFavourites, nextCategories);
    },
    [persistPreferences],
  );

  useEffect(() => {
    if (!currentSession) return undefined;

    let active = true;
    initializedRef.current = false;
    serverPreferencesExistRef.current = false;
    setPreferencesHydrated(false);

    axios
      .post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/form-field-favourites/get`,
        { currentSession },
        { withCredentials: true },
      )
      .then((response) => {
        if (!active) return;

        const data = response?.data?.data || {};
        if (data.exists) {
          const savedFavourites = Array.isArray(data.favourites)
            ? data.favourites
            : [];
          const savedCategories =
            Array.isArray(data.categories) &&
            data.categories.some((category) => category?.key === "other")
              ? data.categories
              : FAVOURITE_CATEGORIES;
          serverPreferencesExistRef.current = true;
          initializedRef.current = true;
          favouritesRef.current = savedFavourites;
          categoriesRef.current = savedCategories;
          setFavourites(savedFavourites);
          setCategories(savedCategories);
          setPreferencesHydrated(true);
          return;
        }

        const legacyFavourites = readStoredFavourites();
        const legacyCategories = readStoredFavouriteCategories();
        if (legacyFavourites !== null) {
          initializedRef.current = true;
          favouritesRef.current = legacyFavourites;
          categoriesRef.current = legacyCategories;
          setFavourites(legacyFavourites);
          setCategories(legacyCategories);
          setPreferencesHydrated(true);
          persistPreferences(legacyFavourites, legacyCategories, () => {
            try {
              window.localStorage.removeItem(FAVOURITES_STORAGE_KEY);
              window.localStorage.removeItem(FAVOURITE_CATEGORIES_STORAGE_KEY);
            } catch {
              // The database is now the source of truth even if cleanup is blocked.
            }
          });
        } else {
          categoriesRef.current = legacyCategories;
          setCategories(legacyCategories);
          setPreferencesHydrated(true);
        }
      })
      .catch(() => {
        if (!active) return;
        initializedRef.current = true;
        setPreferencesHydrated(true);
        onNotifyRef.current?.(
          "error",
          "Your saved favorites could not be loaded.",
        );
      });

    return () => {
      active = false;
    };
  }, [currentSession, persistPreferences]);

  useEffect(() => {
    if (
      initializedRef.current ||
      serverPreferencesExistRef.current ||
      !preferencesHydrated ||
      availableFields.length === 0
    ) {
      return;
    }

    initializedRef.current = true;
    const defaultFavourites = createDefaultFavourites(availableFields);
    commitPreferences(defaultFavourites, categoriesRef.current);
  }, [availableFields, commitPreferences, preferencesHydrated]);

  useEffect(() => {
    const activeCategories = new Set(
      automaticCategoryKey ? automaticCategoryKey.split("|") : [],
    );

    const previousCategories = previousAutomaticCategoriesRef.current;

    const newlyActivated = [...activeCategories].filter(
      (category) => !previousCategories.has(category),
    );

    const deactivated = [...previousCategories].filter(
      (category) => !activeCategories.has(category),
    );

    if (newlyActivated.length > 0) {
      setExpanded(true);
    }

    if (newlyActivated.length > 0 || deactivated.length > 0) {
      setExpandedCategories((previous) => {
        const next = new Set(previous);

        deactivated.forEach((category) => next.delete(category));
        newlyActivated.forEach((category) => next.add(category));

        return next;
      });
    }

    previousAutomaticCategoriesRef.current = activeCategories;
  }, [automaticCategoryKey]);

  const categoryMap = useMemo(
    () =>
      categories.reduce((result, category) => {
        result[category.key] = favourites.filter(
          (favourite) => favourite.category === category.key,
        );
        return result;
      }, {}),
    [favourites, categories],
  );

  const [favouriteSearch, setFavouriteSearch] = useState("");

  const filteredFavourites = useMemo(() => {
    const query = normalizeText(favouriteSearch);
    if (!query) return favourites;

    const categoryLabels = new Map(categories.map((c) => [c.key, c.label]));
    const fieldLabels = new Map(
      availableFields.map((f) => [String(getFieldId(f)), getFieldLabel(f)]),
    );

    return favourites.filter((favourite) =>
      normalizeText(
        `${favourite.label} ${fieldLabels.get(String(favourite.fieldId)) ?? ""} ${
          categoryLabels.get(favourite.category) ?? ""
        }`,
      ).includes(query),
    );
  }, [favourites, categories, availableFields, favouriteSearch]);

  const resolveField = (fieldId) =>
    availableFields.find(
      (field) => String(getFieldId(field)) === String(fieldId),
    );

  const handleDraftFieldChange = (fieldId) => {
    const field = resolveField(fieldId);
    if (field) {
      tooltipLabelInputRef.current?.setValue(getFieldLabel(field));
    }
    setDraftFavourite((previous) => ({
      ...previous,
      fieldId,
      label: field ? getFieldLabel(field) : previous.label,
    }));
  };

  const handleAddFavourite = () => {
    const field = resolveField(draftFavourite.fieldId);
    if (!field) return;

    const previous = favouritesRef.current;
    const alreadyExists = previous.some(
      (item) =>
        String(item.fieldId) === String(draftFavourite.fieldId) &&
        item.category === draftFavourite.category,
    );

    if (alreadyExists) return;

    const nextFavourites = [
      ...previous,
      {
        ...draftFavourite,
        id: `favourite-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        label:
          tooltipLabelInputRef.current?.getValue().trim() ||
          getFieldLabel(field),
      },
    ];
    commitPreferences(nextFavourites, categoriesRef.current);

    setExpanded(true);
    setExpandedCategories((previous) =>
      new Set(previous).add(draftFavourite.category),
    );

    setDraftFavourite((previous) => ({
      ...previous,
      fieldId: "",
      label: "",
    }));
    tooltipLabelInputRef.current?.setValue("");
  };

  const handleFavouriteChange = (id, key, value) => {
    const nextFavourites = favouritesRef.current.map((favourite) =>
      favourite.id === id ? { ...favourite, [key]: value } : favourite,
    );
    commitPreferences(nextFavourites, categoriesRef.current);
  };

  const handleRemoveFavourite = (id) => {
    const nextFavourites = favouritesRef.current.filter(
      (favourite) => favourite.id !== id,
    );
    favouritesRef.current = nextFavourites;
    setFavourites(nextFavourites);
    persistFavouriteDeletion(id);
  };

  const handleDrop = (targetFavourite, sourceId = draggedFavouriteId) => {
    if (loading || !sourceId || sourceId === targetFavourite.id) return;

    const previous = favouritesRef.current;
    const sourceIndex = previous.findIndex((item) => item.id === sourceId);

    const targetIndex = previous.findIndex(
      (item) => item.id === targetFavourite.id,
    );

    if (sourceIndex < 0 || targetIndex < 0) return;

    if (previous[sourceIndex].category !== previous[targetIndex].category) {
      return;
    }

    const next = [...previous];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);

    commitPreferences(next, categoriesRef.current);

    setDraggedFavouriteId(null);
  };

  const handleSelectFavourite = (favourite) => {
    const field = resolveField(favourite.fieldId);
    if (field) onFavouriteSelect?.(field, favourite);
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories((previous) => {
      const next = new Set(previous);
      if (next.has(categoryKey)) next.delete(categoryKey);
      else next.add(categoryKey);
      return next;
    });
  };

  const populatedCategories = categories.filter(
    (category) => (categoryMap[category.key]?.length ?? 0) > 0,
  );

  const handleAddCategory = () => {
    const label = newCategoryInputRef.current?.getValue().trim() ?? "";

    if (!label) {
      setCategoryError("Enter a category name.");
      return;
    }

    const alreadyExists = categories.some(
      (category) => normalizeText(category.label) === normalizeText(label),
    );

    if (alreadyExists) {
      setCategoryError("This category already exists.");
      return;
    }

    const key = `custom-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    const nextCategories = [
      ...categoriesRef.current,
      { key, label, color: newCategoryColor },
    ];
    commitPreferences(favouritesRef.current, nextCategories);

    setExpandedCategories((previous) => new Set(previous).add(key));

    setDraftFavourite((previous) => ({
      ...previous,
      category: key,
    }));

    newCategoryInputRef.current?.setValue("");
    setNewCategoryColor("#64748b");
    setCategoryError("");
  };

  const handleRemoveCategory = (categoryKey) => {
    if (categoryKey === "other") return;

    // Preserve shortcuts by moving them into Other.
    const nextFavourites = favouritesRef.current.map((favourite) =>
      favourite.category === categoryKey
        ? { ...favourite, category: "other" }
        : favourite,
    );
    const nextCategories = categoriesRef.current.filter(
      (category) => category.key !== categoryKey,
    );
    commitPreferences(nextFavourites, nextCategories);

    setExpandedCategories((previous) => {
      const next = new Set(previous);
      next.delete(categoryKey);
      next.add("other");
      return next;
    });

    setDraftFavourite((previous) =>
      previous.category === categoryKey
        ? { ...previous, category: "other" }
        : previous,
    );
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={(_, nextExpanded) => setExpanded(nextExpanded)}
        disableGutters
        elevation={0}
        sx={{
          borderTop: "1px solid #e0e0e0",
          borderRadius: "0 !important",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          component="div"
          expandIcon={<ExpandMoreIcon fontSize="small" />}
          sx={{
            minHeight: 42,
            px: 2,
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              gap: 1.25,
              flexWrap: "wrap",
              my: 0.75,
            },
          }}
        >
          <StarOutlineIcon sx={{ fontSize: 18, color: "warning.main" }} />
          <Typography variant="body2" fontWeight={700}>
            Favorites
          </Typography>
          <Chip label={favourites.length} size="small" sx={{ height: 20 }} />
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<TuneIcon sx={{ fontSize: 15 }} />}
            onClick={(event) => {
              event.stopPropagation();
              setCustomizeOpen(true);
            }}
            onFocus={(event) => event.stopPropagation()}
            sx={{ textTransform: "none", py: 0.25 }}
          >
            Customize
          </Button>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            px: 2,
            pt: 0.5,
            pb: 1.5,
            maxHeight: "min(150px, 25vh)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {populatedCategories.length === 0 ? (
            <Box
              sx={{
                border: "1px dashed #cbd5e1",
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Add commonly used form fields to create your shortcuts.
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCustomizeOpen(true)}
                sx={{ mt: 0.5, textTransform: "none" }}
              >
                Add favorite
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridAutoFlow: "column",
                gridAutoColumns: "minmax(225px, 1fr)",
                gap: 1,
                minWidth: 0,
                overflowX: "auto",
                pb: 1,
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: 3,
                },
              }}
            >
              {populatedCategories.map((category) => {
                const categoryFavourites = categoryMap[category.key];
                const categoryExpanded = expandedCategories.has(category.key);
                const isAutomatic = automaticCategories.includes(category.key);

                return (
                  <Accordion
                    key={category.key}
                    expanded={categoryExpanded}
                    onChange={() => toggleCategory(category.key)}
                    disableGutters
                    elevation={0}
                    sx={{
                      alignSelf: "start",
                      minWidth: 0,
                      border: "1px solid #dbe3ec",
                      borderLeft: `3px solid ${category.color}`,
                      borderRadius: "8px !important",
                      overflow: "hidden",
                      boxShadow: isAutomatic
                        ? `0 0 0 2px ${category.color}22`
                        : "none",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        minHeight: 34,
                        px: 1,
                        "&.Mui-expanded": { minHeight: 34 },
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 0.5,
                          my: 0.5,
                        },
                        "& .MuiAccordionSummary-content.Mui-expanded": {
                          my: 0.5,
                        },
                      }}
                    >
                      <Typography variant="caption" fontWeight={700}>
                        {category.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {categoryFavourites.length}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
                      {categoryFavourites.length === 0 && (
                        <Box sx={{ py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            No favorites added to this category yet.
                          </Typography>

                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              setDraftFavourite((previous) => ({
                                ...previous,
                                category: category.key,
                              }));
                              setCustomizeOpen(true);
                            }}
                            sx={{
                              display: "flex",
                              mt: 0.5,
                              textTransform: "none",
                            }}
                          >
                            Add favorite
                          </Button>
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        {categoryFavourites.map((favourite) => (
                          <FavouriteDragItem
                            key={favourite.id}
                            favourite={favourite}
                            label={
                              resolveField(favourite.fieldId)
                                ? getFieldLabel(resolveField(favourite.fieldId))
                                : favourite.label
                            }
                            disabled={
                              loading || !resolveField(favourite.fieldId)
                            }
                            onReorder={handleDrop}
                          >
                            <Tooltip
                              title={
                                resolveField(favourite.fieldId)
                                  ? getFieldLabel(
                                      resolveField(favourite.fieldId),
                                    )
                                  : `${favourite.label} — field unavailable`
                              }
                              arrow
                              placement="top"
                              enterDelay={250}
                              disableInteractive
                            >
                              <span style={{ display: "inline-flex" }}>
                                <IconButton
                                  size="small"
                                  disabled={
                                    loading || !resolveField(favourite.fieldId)
                                  }
                                  aria-label={
                                    resolveField(favourite.fieldId)
                                      ? getFieldLabel(
                                          resolveField(favourite.fieldId),
                                        )
                                      : favourite.label
                                  }
                                  onClick={() =>
                                    handleSelectFavourite(favourite)
                                  }
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    border: "1px solid #d0d7de",
                                    borderRadius: 1.5,
                                    color: category.color,
                                    cursor: loading ? "default" : "grab",
                                    "&:hover": {
                                      backgroundColor: `${category.color}12`,
                                      borderColor: category.color,
                                    },
                                    "&:active": {
                                      cursor: "grabbing",
                                    },
                                  }}
                                >
                                  {FAVOURITE_ICON_OPTIONS[favourite.icon]
                                    ?.icon ??
                                    FAVOURITE_ICON_OPTIONS.description.icon}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </FavouriteDragItem>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        maxWidth="md"
        fullWidth
        keepMounted
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StarOutlineIcon color="warning" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              Customize favorites
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add shortcuts, choose their icon and category, or drag icons in
              the favorites bar to reorder them.
            </Typography>
          </Box>
          <IconButton onClick={() => setCustomizeOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent
          onScroll={() => {
            if (autocompleteOpen) {
              setAutocompleteOpen(false);
              autocompleteInputRef.current?.blur();
            }
          }}
          sx={{ overflow: "auto" }}
        >
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            Categories
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              mb: 1.5,
            }}
          >
            <FavouriteDraftTextField
              ref={newCategoryInputRef}
              size="small"
              fullWidth
              label="New category"
              placeholder="For example, Private Provider"
              error={Boolean(categoryError)}
              helperText={categoryError}
              onChange={() => {
                if (categoryError) setCategoryError("");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleAddCategory();
                }
              }}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <TextField
              select
              size="small"
              label="Category colour"
              value={newCategoryColor}
              onChange={(event) => setNewCategoryColor(event.target.value)}
              sx={{ width: 150, flexShrink: 0 }}
            >
              {CATEGORY_COLOR_OPTIONS.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        bgcolor: value,
                        flexShrink: 0,
                      }}
                    />
                    {label}
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCategory}
              sx={{
                height: 50,
                minWidth: 140,
                px: 2,
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              Add category
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {categories.map((category) => (
              <Chip
                key={category.key}
                label={category.label}
                variant="outlined"
                onDelete={
                  category.key === "other"
                    ? undefined
                    : () => handleRemoveCategory(category.key)
                }
              />
            ))}
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Removing a category moves its favorites to Other.
          </Typography>

          <Divider sx={{ my: 2.5 }} />
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            Add a favorite
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(180px, 1.4fr) minmax(150px, 1fr) minmax(160px, 1fr) auto",
              },
              gap: 1,
              alignItems: "center",
            }}
          >
            <Autocomplete
              size="small"
              fullWidth
              open={autocompleteOpen}
              onOpen={() => setAutocompleteOpen(true)}
              onClose={() => setAutocompleteOpen(false)}
              options={availableFields}
              value={resolveField(draftFavourite.fieldId) ?? null}
              getOptionLabel={getFavouriteOptionLabel}
              isOptionEqualToValue={(option, value) =>
                String(getFieldId(option)) === String(getFieldId(value))
              }
              onChange={(_, field) =>
                handleDraftFieldChange(field ? getFieldId(field) : "")
              }
              renderOption={(props, field) => (
                <li {...props} key={String(getFieldId(field))}>
                  {getFavouriteOptionLabel(field)}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={autocompleteInputRef}
                  label="Form field"
                  placeholder="Search fields"
                />
              )}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={draftFavourite.category}
                label="Category"
                onChange={(event) =>
                  setDraftFavourite((previous) => ({
                    ...previous,
                    category: event.target.value,
                  }))
                }
              >
                {categories.map((category) => (
                  <MenuItem key={category.key} value={category.key}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Icon</InputLabel>
              <Select
                value={draftFavourite.icon}
                label="Icon"
                onChange={(event) =>
                  setDraftFavourite((previous) => ({
                    ...previous,
                    icon: event.target.value,
                  }))
                }
              >
                {Object.entries(FAVOURITE_ICON_OPTIONS).map(([key, option]) => (
                  <MenuItem key={key} value={key}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {option.icon}
                      <span>{option.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!draftFavourite.fieldId}
              onClick={handleAddFavourite}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              Add
            </Button>
          </Box>
          <FavouriteDraftTextField
            ref={tooltipLabelInputRef}
            label="Custom tooltip label (optional)"
            size="small"
            fullWidth
            sx={{ mt: 1 }}
          />

          <Divider sx={{ my: 2.5 }} />
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            Current favorites
          </Typography>
          <FavouriteSearchField onSearch={setFavouriteSearch} />
          {favourites.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No favorites have been added yet.
            </Typography>
          ) : filteredFavourites.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No favorites match your search.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {filteredFavourites.map((favourite) => (
                <Box
                  key={favourite.id}
                  onDragOver={(event) => {
                    if (draggedFavouriteId) event.preventDefault();
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleDrop(favourite);
                  }}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "auto 1fr auto",
                      sm: "auto minmax(160px, 1.5fr) minmax(150px, 1fr) minmax(145px, 1fr) auto",
                    },
                    gap: 1,
                    alignItems: "center",
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    p: 1,
                  }}
                >
                  <Box
                    component="span"
                    draggable
                    title="Drag to reorder within this category"
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", favourite.id);
                      setDraggedFavouriteId(favourite.id);
                    }}
                    onDragEnd={() => setDraggedFavouriteId(null)}
                    sx={{ display: "inline-flex", cursor: "grab" }}
                  >
                    <DragIndicatorIcon color="action" />
                  </Box>
                  <TextField
                    value={favourite.label}
                    label="Tooltip label"
                    size="small"
                    onChange={(event) =>
                      handleFavouriteChange(
                        favourite.id,
                        "label",
                        event.target.value,
                      )
                    }
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={favourite.category}
                      label="Category"
                      onChange={(event) =>
                        handleFavouriteChange(
                          favourite.id,
                          "category",
                          event.target.value,
                        )
                      }
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.key} value={category.key}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Icon</InputLabel>
                    <Select
                      value={favourite.icon}
                      label="Icon"
                      onChange={(event) =>
                        handleFavouriteChange(
                          favourite.id,
                          "icon",
                          event.target.value,
                        )
                      }
                    >
                      {Object.entries(FAVOURITE_ICON_OPTIONS).map(
                        ([key, option]) => (
                          <MenuItem key={key} value={key}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {option.icon}
                              <span>{option.label}</span>
                            </Stack>
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </FormControl>
                  <Tooltip title="Remove favorite">
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveFavourite(favourite.id)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            onClick={() => setCustomizeOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FavouritesBar;
